import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

const BROKER_TOKEN_KEY = 'weddingly_broker_token';
const BROKER_USER_KEY = 'weddingly_broker_user';

@Injectable({ providedIn: 'root' })
export class BrokerService {
  private apiUrl = `${environment.apiUrl}/broker`;
  private brokerUser$ = new BehaviorSubject<any>(this.getStoredBroker());

  constructor(private http: HttpClient) {}

  private headers(): HttpHeaders {
    const token = localStorage.getItem(BROKER_TOKEN_KEY) || '';
    return new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` });
  }

  // ─── Auth ──────────────────────────────────────────────────────────
  register(data: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/register`, data).pipe(
      tap(res => {
        if (res.status === 'success' && res.data?.token) {
          localStorage.setItem(BROKER_TOKEN_KEY, res.data.token);
          localStorage.setItem(BROKER_USER_KEY, JSON.stringify(res.data.broker));
          this.brokerUser$.next(res.data.broker);
        }
      })
    );
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/login`, { email, password }).pipe(
      tap(res => {
        if (res.status === 'success') {
          localStorage.setItem(BROKER_TOKEN_KEY, res.data.token);
          localStorage.setItem(BROKER_USER_KEY, JSON.stringify(res.data.broker));
          this.brokerUser$.next(res.data.broker);
        }
      })
    );
  }

  logout(): void {
    localStorage.removeItem(BROKER_TOKEN_KEY);
    localStorage.removeItem(BROKER_USER_KEY);
    this.brokerUser$.next(null);
  }

  isLoggedIn(): boolean { return !!localStorage.getItem(BROKER_TOKEN_KEY); }

  getStoredBroker(): any {
    try { return JSON.parse(localStorage.getItem(BROKER_USER_KEY) || 'null'); }
    catch { return null; }
  }

  getBrokerUser$() { return this.brokerUser$.asObservable(); }

  // ─── Profile & Bank Details ────────────────────────────────────────
  getProfile(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/profile`, { headers: this.headers() }).pipe(
      tap(res => {
        if (res.status === 'success' && res.data) {
          localStorage.setItem(BROKER_USER_KEY, JSON.stringify(res.data));
          this.brokerUser$.next(res.data);
        }
      })
    );
  }

  updateBankDetails(data: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/bank-details`, data, { headers: this.headers() });
  }

  // ─── Candidates (Shell Profiles) ───────────────────────────────────
  createCandidate(data: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/candidates`, data, { headers: this.headers() });
  }

  updateCandidate(id: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/candidates/${id}`, data, { headers: this.headers() });
  }

  getCandidates(params: any = {}): Observable<any> {
    const query = new URLSearchParams(params).toString();
    return this.http.get<any>(`${this.apiUrl}/candidates?${query}`, { headers: this.headers() });
  }

  getCandidateViews(candidateId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/candidates/${candidateId}/views`, { headers: this.headers() });
  }

  // ─── Commissions & Payouts ─────────────────────────────────────────
  getCommissions(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/commissions`, { headers: this.headers() });
  }

  requestPayout(amount: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/payout`, { amount }, { headers: this.headers() });
  }

  // ─── Profile Claim (Public) ────────────────────────────────────────
  initiateClaim(profile_id: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/claim/initiate`, { profile_id });
  }

  verifyClaim(profile_id: string, otp: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/claim/verify`, { profile_id, otp });
  }

  // ─── Broker Subscription Plans & Leads ─────────────────────────────
  getBrokerPlans(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/plans`);
  }

  getSubscription(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/subscription`, { headers: this.headers() });
  }

  subscribePlan(plan_code: string, amount: number = 0, payment_id: string = '', order_id: string = ''): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/subscribe`, { plan_code, amount, payment_id, order_id }, { headers: this.headers() });
  }

  createOrder(plan_code: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/create-order`, { plan_code }, { headers: this.headers() });
  }

  verifyPayment(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/verify-payment`, payload, { headers: this.headers() });
  }

  getLeads(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/leads`, { headers: this.headers() });
  }

  updateLeadStatus(id: string, status: string, notes: string = ''): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/leads/${id}/status`, { status, notes }, { headers: this.headers() });
  }
}
